import {Timer} from '@gongxigames/framework/core';
import {ReelsModule} from '@gongxigames/framework/slot/modules/reels/ReelsModule';
import {ReelsEvents} from '@gongxigames/framework/slot/modules/reels/ReelsEvents';
import {AbstractReelsView} from '@gongxigames/framework/slot/modules/reels/AbstractReelsView';
import {SpinButtonEvents} from '@gongxigames/framework/slot/modules/spinbutton';
import {LogicEvents} from '@gongxigames/framework/slot/services/logic/LogicEvents';
import {ModuleState} from '@gongxigames/framework/slot/fragments/module/ModuleState';

export class ReelsSpinningState extends ModuleState<AbstractReelsView, ReelsModule> {
    protected _stopTimer?: Timer;
    protected _expectingResponse = false;

    protected setupEvents(): void {
        this.setEventHandler(SpinButtonEvents.SPIN_BUTTON_PRESSED, this.onTryinToStopSpinningWithSpinButton.bind(this));
        this.setEventHandler(LogicEvents.SPIN_RESPONSE, this.onSpinResponseReceived.bind(this));
        this.setEventHandler(LogicEvents.FREE_SPIN_RESPONSE, this.onSpinResponseReceived.bind(this));
        this.setEventHandler(LogicEvents.RESPIN_RESPONSE, this.onSpinResponseReceived.bind(this));
        this.setEventHandler(LogicEvents.MINI_SPIN_RESPONSE, this.onSpinResponseReceived.bind(this));
    }

    public onEnterState(): void {
        this.module.resetParameters();
        this.view.showAllSymbols();
        this.startReels();

        this._expectingResponse = false;

        const {FIRST_REEL_STOP_TIME, getReelFullStoppingTime} = this.view.reelConstants;
        // we use Math.max because we can not start stopping the reels faster than immediately that theoretically can be in case
        // when FIRST_REEL_STOP_TIME less than time for stopping the reel
        const stopTimerDuration = this.module.isTurboSpin() ? 0 : Math.max(FIRST_REEL_STOP_TIME - getReelFullStoppingTime(), 0);

        this._stopTimer = new Timer(this.onBeforeStopTimer.bind(this), stopTimerDuration, true);
        this.module.updateManager.add(this._stopTimer);

        this.module.eventManager.emit(ReelsEvents.REELS_SPINNING_STARTED);
    }

    public onLeaveState(): void {
        this.clearStoppingTimer();
    }

    protected clearStoppingTimer(): void {
        this.module.updateManager.remove(this._stopTimer!);
        this._stopTimer = undefined;
    }

    protected startReels(): void {
        this.view.startSpinning();
    }

    protected tryInitializeStoppingState(): void {
        if (this.module.isReelsCanBeStopped) {
            this.module.gotoState(ReelsModule.STOPPING_STATE);
        } else {
            this._expectingResponse = true;
        }
    }

    protected onBeforeStopTimer(): void {
        this.tryInitializeStoppingState();
    }

    protected onSpinResponseReceived(): void {
        this.getOutStopPositionsFromResponse();
        this.getReelsWithScatterFromResponse();
        this.view.symbolsWithHitAnimationOnReels = this.getSymbolsFromResponse(this.view.symbolsWithHitAnimation);
        this.view.oversizeSymbolsOnReels = this.getSymbolsFromResponse(this.view.oversizeSymbols);

        if (this._expectingResponse) {
            this._expectingResponse = false;
            this.module.gotoState(ReelsModule.STOPPING_STATE);
        }
    }

    protected onTryinToStopSpinningWithSpinButton(): void {
        this.getOutStopPositionsFromResponse();
        this.module.gotoState(ReelsModule.QUICKSTOP_STATE);
    }

    protected getOutStopPositionsFromResponse(): void {
        this.view.stopPositions = this.module.logic.spinData.reelPositions;
    }

    protected getReelsWithScatterFromResponse(): void {
        const symbolIds = this.module.logic.spinData.symbolIds;
        const reels: number[][] = this.createEmpty();
        let scatterAmount = 0;

        for (let i = 0; i < symbolIds.length; i++) {
            const index = this.getSymbolIndexOnReel(symbolIds[i], this.view.scatterIndex);

            if (index >= 0) {
                scatterAmount++;

                if (scatterAmount + symbolIds.length - i - 1 >= this.minScattersForFreeGames()) {
                    reels[i][index] = this.view.scatterIndex;
                }
            }
        }

        this.view.scattersOnReels = reels;
    }

    protected getSymbolsFromResponse(symbolTypes: number[]): number[][][] {
        const foundSymbols: number[][][] = [];
        const symbols = this.module.logic.spinData.symbolIds;

        for (let reel = 0; reel < symbols.length; reel++) {
            foundSymbols[reel] = [];

            for (let row = 0; row < this.view.reelConstants.ROW_AMOUNT; row++) {
                const symbol = symbols[reel][row];

                if (symbolTypes.indexOf(symbol) !== -1) {
                    if (!foundSymbols[reel][symbol]) {
                        foundSymbols[reel][symbol] = [];
                    }

                    foundSymbols[reel][symbol].push(row);
                }
            }
        }

        return foundSymbols;
    }

    protected getSymbolIndexOnReel(reel: number[], symbol: number): number {
        for (let i = 0; i < reel.length; i++) {
            if (reel[i] === symbol) {
                return i;
            }
        }

        return -1;
    }

    protected createEmpty(): number[][] {
        const symbolIds = this.module.logic.spinData.symbolIds;
        const reels = [];

        for (let i = 0; i < symbolIds.length; i++) {
            reels[i] = [];
        }

        return reels;
    }

    protected minScattersForFreeGames(): number {
        return 3;
    }
}
